import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { browse as spBrowse } from '../../api/sharepointAPI';
import { isoLang } from '../../services/language';

// Kľúč uzla: SharePoint vracia serverRelativeUrl, zdieľaný disk relatívnu cestu `path` (oddeľovač '/').
const relOf = (node) => node.serverRelativeUrl ?? node.path;

// ── Pomocné (single-root model) ───────────────────────────────────────────────
const hasAncestorOrSelf = (rel, set) => {
  for (const r of set) if (rel === r || rel.startsWith(r + '/')) return true;
  return false;
};
// root: null = nič nevybraté; '' = koreň (celý zdieľaný disk); inak cesta priečinka.
const hasRoot = (root) => root !== null && root !== undefined;
const underRoot = (rel, root) => hasRoot(root) && (root === '' ? rel !== '' : rel.startsWith(root + '/'));
const isExcluded = (rel, excluded) => hasAncestorOrSelf(rel, excluded);
const nodeChecked = (rel, root, excluded) => {
  if (isExcluded(rel, excluded)) return false;
  return root === rel || underRoot(rel, root);
};
const hasExcludedDesc = (rel, excluded) => {
  for (const e of excluded) if (rel === '' ? e !== '' : e.startsWith(rel + '/')) return true;
  return false;
};

// Orientačná veľkosť priečinka (zdieľaný disk aj SharePoint). Bez spinnera — objaví sa, až keď je spočítaná;
// obrovský priečinok sa dopočítava na pozadí („počítam…“ + priebežný stav, kým nie je hotový).
const FolderSize = ({ info }) => {
  const [t, i18n] = useTranslation('global');
  if (!info) return null;
  const lang = isoLang(i18n.language);
  const nf0 = new Intl.NumberFormat(lang, { maximumFractionDigits: 0 });
  const nf1 = new Intl.NumberFormat(lang, { maximumFractionDigits: 1 });
  const rule = new Intl.PluralRules(lang).select(info.files);
  const files = t([`folderSizeFiles_${rule}`, 'folderSizeFiles_other'], { n: nf0.format(info.files) });
  const gb = info.bytes / 1024 ** 3;
  const size = gb >= 1024 ? `${nf1.format(gb / 1024)} TB`
    : gb >= 1 ? `${(gb >= 100 ? nf0 : nf1).format(gb)} GB`
      : `${nf0.format(Math.ceil(info.bytes / 1024 ** 2))} MB`;
  if (info.running) {
    const counting = t('folderSizeCounting') || 'počítam…';
    return <span className="sp-node-size running">{info.files ? `${counting} ${files} · ${size}` : counting}</span>;
  }
  const prefix = info.complete ? '≈' : (t('folderSizeMore') || 'viac ako');
  return <span className="sp-node-size">{prefix} {files} · {size}</span>;
};

const SIZE_POLL_MS = 5000;

// Súbory priečinka po stránkach: zdieľaný disk vráti prvú stránku + celkový počet (filesTotal), ďalšie
// sa doťahujú tlačidlom (browseFn s offsetom) — priečinok s desiatkami tisíc súborov nezahltí strom.
// SharePoint filesTotal neposiela → zobrazí sa všetko ako doteraz.
const FileRows = ({ connId, rel, data, browseFn, depth, renderFile }) => {
  const [t, i18n] = useTranslation('global');
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const files = more.length ? data.files.concat(more) : data.files;
  const total = data.filesTotal ?? files.length;
  const left = total - files.length;

  const loadMore = () => {
    if (loading) return;
    setLoading(true); setError(null);
    browseFn(connId, rel, files.length)
      .then(d => {
        const seen = new Set(files.map(relOf));
        setMore(m => m.concat((d.files || []).filter(f => !seen.has(relOf(f)))));
      })
      .catch(e => setError(e?.response?.data?.detail || t('connectionError')))
      .finally(() => setLoading(false));
  };

  const nf = new Intl.NumberFormat(isoLang(i18n.language));
  return (
    <>
      {files.map(renderFile)}
      {left > 0 && (
        <div className="sp-more" style={{ paddingLeft: `${depth * 1.2}em` }}>
          <span className="sp-more-info">{t('filesShown', { shown: nf.format(files.length), total: nf.format(total) })}</span>
          <button type="button" className="sp-more-btn" onClick={loadMore} disabled={loading}>
            {loading ? '…' : t('loadMoreFiles', { n: nf.format(Math.min(data.files.length, left)) })}
          </button>
          {error && <span className="sp-error">{error}</span>}
        </div>
      )}
    </>
  );
};

const FolderNode = ({ connId, node, isFolder, root, excluded, onToggle, depth, browseFn, sizes, onSizeRequest }) => {
  const [t] = useTranslation('global');
  const rel = relOf(node);
  const containsRoot = !!root && (rel === root || root.startsWith(rel + '/'));  // '' (celý disk) žiadny uzol neobsahuje
  const [open, setOpen] = useState(containsRoot);
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cbRef = useRef(null);

  const excludedNode = isExcluded(rel, excluded);
  const checked = nodeChecked(rel, root, excluded);
  const indeterminate = isFolder && checked && hasExcludedDesc(rel, excluded);
  const disabled = !isFolder && !(hasRoot(root) && (rel === root || underRoot(rel, root))); // súbor mimo rootu = neaktívny

  useEffect(() => { if (cbRef.current) cbRef.current.indeterminate = indeterminate; }, [indeterminate]);

  const loadChildren = () => {
    if (children || loading) return;
    setLoading(true); setError(null);
    browseFn(connId, rel)
      .then(d => setChildren({ folders: d.folders || [], files: d.files || [], filesTotal: d.filesTotal }))
      .catch(e => setError(e?.response?.data?.detail || t('connectionError')))
      .finally(() => setLoading(false));
  };
  // klik na priečinok (rozbalenie) = aj dopyt na jeho veľkosť; automatické rozbalenie nižšie nie
  const toggleOpen = () => { const n = !open; setOpen(n); if (n) { loadChildren(); onSizeRequest(rel); } };
  useEffect(() => {
    if (isFolder && containsRoot) {
      setOpen(true);
      loadChildren();
    }
  }, [containsRoot]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="sp-node">
      <div className={`sp-node-row${root === rel ? ' sp-node-selected' : ''}`} style={{ paddingLeft: `${depth * 1.2}em` }}>
        {isFolder
          ? <span className="sp-caret material-symbols-outlined" onClick={toggleOpen}>{open ? 'expand_more' : 'chevron_right'}</span>
          : <span className="sp-caret-spacer" />}
        <input
          ref={cbRef}
          type="checkbox"
          className={`sp-check${excludedNode ? ' sp-excluded' : ''}`}
          checked={checked}
          disabled={disabled}
          onChange={() => onToggle(rel, isFolder)}
        />
        <span className={`material-symbols-outlined sp-icon${isFolder ? '' : ' sp-file'}`}>{isFolder ? 'folder' : 'description'}</span>
        <span className={`sp-name${excludedNode ? ' sp-name-excluded' : ''}`} onClick={isFolder ? toggleOpen : undefined}>{node.name}</span>
        {isFolder && <FolderSize info={sizes[rel]} />}
      </div>
      {open && (
        <div className="sp-children">
          {loading && <div className="sp-loading" style={{ paddingLeft: `${(depth + 1) * 1.2}em` }}>…</div>}
          {error && <div className="sp-error" style={{ paddingLeft: `${(depth + 1) * 1.2}em` }}>{error}</div>}
          {children && children.folders.map(f => (
            <FolderNode key={relOf(f)} connId={connId} node={f} isFolder root={root} excluded={excluded} onToggle={onToggle} depth={depth + 1} browseFn={browseFn} sizes={sizes} onSizeRequest={onSizeRequest} />
          ))}
          {children && (
            <FileRows connId={connId} rel={rel} data={children} browseFn={browseFn} depth={depth + 1} renderFile={f => (
              <FolderNode key={relOf(f)} connId={connId} node={f} isFolder={false} root={root} excluded={excluded} onToggle={onToggle} depth={depth + 1} browseFn={browseFn} sizes={sizes} onSizeRequest={onSizeRequest} />
            )} />
          )}
          {children && children.folders.length === 0 && children.files.length === 0 && !loading && (
            <div className="sp-empty" style={{ paddingLeft: `${(depth + 1) * 1.2}em` }}>—</div>
          )}
        </div>
      )}
    </div>
  );
};

// rootData = { folders, files } (načíta rodičovská obrazovka pri teste pripojenia)
// browseFn(connId, path) → Promise<{folders, files}>; predvolene SharePoint.
// rootLabel: ak je zadaný, zobrazí sa nad stromom riadok pre koreň (rel '') — celý zdieľaný disk,
// aby sa dali synchronizovať aj súbory priamo v koreni a jednotlivé z nich vyňať.
// sizeFn(connId, path) → Promise<{files, bytes, complete, running}>: voliteľná orientačná veľkosť priečinka
// (zdieľaný disk aj SharePoint) — pýta sa pri kliknutí na priečinok a pre vybraný priečinok, každý najviac raz
// (kým sa ešte počíta — running —, pýta sa znova po SIZE_POLL_MS).
const FolderTree = ({ connId, rootData, root, excluded, onToggle, browseFn = spBrowse, rootLabel, sizeFn }) => {
  const rootCbRef = useRef(null);
  const rootChecked = root === '' && !excluded.has('');
  const rootIndeterminate = rootChecked && hasExcludedDesc('', excluded);
  useEffect(() => { if (rootCbRef.current) rootCbRef.current.indeterminate = rootIndeterminate; }, [rootIndeterminate]);

  const [sizes, setSizes] = useState({});
  const requestedSizes = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const fetchSize = (rel) => {
    sizeFn(connId, rel)
      .then(info => {
        if (!mounted.current) return;
        setSizes(current => ({ ...current, [rel]: info }));
        if (info.running) setTimeout(() => { if (mounted.current) fetchSize(rel); }, SIZE_POLL_MS);
      })
      .catch(() => requestedSizes.current.delete(rel));   // chyba = nič nezobraziť, ďalší klik skúsi znova
  };
  const requestSize = (rel) => {
    if (!sizeFn || requestedSizes.current.has(rel)) return;
    requestedSizes.current.add(rel);
    fetchSize(rel);
  };
  useEffect(() => { if (hasRoot(root)) requestSize(root); }, [root]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!rootData) return null;
  const depth0 = rootLabel ? 1 : 0;
  return (
    <div className="sp-tree">
      {rootLabel && (
        <div className="sp-node">
          <div className={`sp-node-row${root === '' ? ' sp-node-selected' : ''}`}>
            <span className="sp-caret material-symbols-outlined">expand_more</span>
            <input ref={rootCbRef} type="checkbox" className="sp-check" checked={rootChecked} onChange={() => onToggle('', true)} />
            <span className="material-symbols-outlined sp-icon">folder_shared</span>
            <span className="sp-name" onClick={() => onToggle('', true)}>{rootLabel}</span>
            <FolderSize info={sizes['']} />
          </div>
        </div>
      )}
      {(rootData.folders || []).map(f => (
        <FolderNode key={relOf(f)} connId={connId} node={f} isFolder root={root} excluded={excluded} onToggle={onToggle} depth={depth0} browseFn={browseFn} sizes={sizes} onSizeRequest={requestSize} />
      ))}
      <FileRows connId={connId} rel="" data={{ files: rootData.files || [], filesTotal: rootData.filesTotal }} browseFn={browseFn} depth={depth0} renderFile={f => (
        <FolderNode key={relOf(f)} connId={connId} node={f} isFolder={false} root={root} excluded={excluded} onToggle={onToggle} depth={depth0} browseFn={browseFn} sizes={sizes} onSizeRequest={requestSize} />
      )} />
      {(rootData.folders || []).length === 0 && (rootData.files || []).length === 0 && <div className="sp-empty">—</div>}
    </div>
  );
};

export default FolderTree;
